/**
 * Client-side TypeScript interfaces.
 * Re-exports shared types and adds UI-specific types.
 */

export type {
  MidiNote,
  MidiTrack,
  OnsetEvent,
  WaveformData,
  Difficulty,
  HitRating,
  HitScore,
  TrackRecord,
  ScoreRecord,
  UserRecord,
  UserProgress,
} from '../../../shared/types/midi';

// ---------------------------------------------------------------------------
// UI / Game-specific types
// ---------------------------------------------------------------------------

/** A note as it exists in the game world with rendering state */
export interface GameNote {
  id: string;
  /** Time in ms from track start when the note should be hit */
  time: number;
  /** Lane index 0–5 */
  lane: number;
  /** Current Y position on the canvas (updated each frame, falls downward) */
  y: number;
  /** Has this note already been judged? */
  judged: boolean;
  /** The rating assigned when judged */
  rating?: 'PERFECT' | 'GOOD' | 'MISS';
}

/** A visual hit-feedback particle */
export interface HitParticle {
  id: string;
  x: number;
  y: number;
  lane: number;
  rating: 'PERFECT' | 'GOOD' | 'MISS';
  /** Opacity 0–1, decreases each frame */
  opacity: number;
  /** Frame counter for animation */
  age: number;
}

/** Current game state */
export type GamePhase =
  | 'idle'
  | 'countdown'
  | 'playing'
  | 'paused'
  | 'finished';

/** A snapshot of in-flight game state */
export interface GameState {
  phase: GamePhase;
  score: number;
  combo: number;
  maxCombo: number;
  perfectCount: number;
  goodCount: number;
  missCount: number;
  /** Current song time in ms */
  currentTime: number;
  notes: GameNote[];
  particles: HitParticle[];
}

/** Track listing item (without full MIDI data) */
export interface TrackSummary {
  id: string;  // UUID
  title: string;
  artist?: string;
  bpm?: number;
  duration?: number;
  sectionStart?: number;
  sectionEnd?: number;
  difficulty: string;
  createdAt: string;
}

/** Full track detail (includes MIDI data) */
export interface TrackDetail extends TrackSummary {
  midiData: import('../../../shared/types/midi').MidiTrack;
  waveformData: import('../../../shared/types/midi').WaveformData;
  processedFilePath?: string;
}

/** Keyboard key to lane mapping */
export const KEY_TO_LANE: Record<string, number> = {
  s: 0,
  d: 1,
  f: 2,
  j: 3,
  k: 4,
  l: 5,
};

export const LANE_KEYS = ['s', 'd', 'f', 'j', 'k', 'l'];

export const LANE_COLORS = [
  '#00d4ff', // cyan
  '#bf00ff', // purple
  '#00ff88', // green
  '#ff0080', // pink
  '#ffee00', // yellow
  '#ff6600', // orange
];

// ---------------------------------------------------------------------------
// Mobile/Touch types
// ---------------------------------------------------------------------------

/** Input mode for gameplay */
export type InputMode = 'keyboard' | 'touch' | 'auto';

/** Touch zone configuration */
export interface TouchZoneConfig {
  lane: number;
  label: string;
  color: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Device type detection */
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/** Screen orientation */
export type ScreenOrientation = 'portrait' | 'landscape';
